import { isLocal } from '../../environment';
import {
  ArraySchema,
  BooleanSchema,
  Errors,
  IntegerSchema,
  MessageWithArgs,
  NullSchema,
  NumberSchema,
  ObjectSchema,
  Schema,
  StringSchema,
  isArraySchema,
  isBooleanSchema,
  isIntegerSchema,
  isNullSchema,
  isNumberSchema,
  isObjectSchema,
  isStringSchema
} from '../types';
import { evaluateSubschema } from './evaluateSchema';
import { FORMAT_VALIDATORS } from './validators';

function isBlank(value: unknown) {
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return value == null;
}

function validateString(
  schema: StringSchema,
  value: string | null | undefined,
  required: boolean
): Errors<StringSchema> {
  if (typeof schema.const !== 'undefined' && schema.const !== value) {
    return [
      {
        message: getMessageKey('errors.const', schema),
        args: { value: schema.const }
      }
    ];
  }

  const errors = [];
  if (isBlank(value)) {
    if (!required) {
      return [];
    }

    errors.push(getMessageKey('errors.required', schema));
    return errors;
  }

  const length = value === null || value === undefined ? 0 : value.length;

  if (schema.minLength && length < schema.minLength) {
    errors.push({
      message: getMessageKey('errors.minLength', schema),
      args: {
        minLength: schema.minLength
      }
    });
  }

  if (schema.maxLength && length > schema.maxLength) {
    errors.push({
      message: getMessageKey('errors.maxLength', schema),
      args: {
        maxLength: schema.maxLength
      }
    });
  }

  if (schema.enum && !schema.enum.includes(value!)) {
    errors.push({
      message: getMessageKey('errors.oneOf', schema),
      args: {
        values: schema.enum
      }
    });
  }

  if (schema.pattern) {
    const expression = new RegExp(schema.pattern);
    if (!expression.test(value!)) {
      errors.push(getMessageKey('errors.invalidPattern', schema));
    }
  }

  if (schema.format) {
    const formatValidationFunction = FORMAT_VALIDATORS[schema.format];

    if (isLocal && !formatValidationFunction) {
      throw new Error(`${schema.format} format validator is not implemented.`);
    }

    if (formatValidationFunction && !formatValidationFunction(value!)) {
      errors.push(
        getMessageKey(`errors.invalidFormat.${schema.format}`, schema)
      );
    }
  }

  return errors;
}

function validateBoolean(
  schema: BooleanSchema,
  value: boolean | null | undefined,
  required: boolean
): Errors<BooleanSchema> {
  if (typeof schema.const !== 'undefined' && schema.const !== value) {
    return [
      {
        message: getMessageKey('errors.const', schema),
        args: { value: schema.const }
      }
    ];
  }

  const errors = [];

  if (isBlank(value)) {
    if (!required) {
      return [];
    }

    errors.push(getMessageKey('errors.required', schema));
    return errors;
  }

  if (schema.enum && !schema.enum.includes(value!)) {
    errors.push({
      message: getMessageKey('errors.oneOf', schema),
      args: {
        values: schema.enum
      }
    });
  }

  return errors;
}

function validateNumber(
  schema: NumberSchema,
  value: string | number | null | undefined,
  required: boolean
): Errors<NumberSchema> {
  if (typeof schema.const !== 'undefined' && schema.const !== value) {
    return [
      {
        message: getMessageKey('errors.const', schema),
        args: { value: schema.const }
      }
    ];
  }

  const errors = [];

  if (isBlank(value) || Number.isNaN(value as any)) {
    if (!required) {
      return [];
    }

    errors.push(getMessageKey('errors.required', schema));
    return errors;
  }

  const numericValue = parseFloat(`${value}`);

  if (schema.minimum && numericValue < schema.minimum) {
    errors.push({
      message: getMessageKey('errors.minimum', schema),
      args: {
        minimum: schema.minimum
      }
    });
  }

  if (schema.maximum && numericValue > schema.maximum) {
    errors.push({
      message: getMessageKey('errors.maximum', schema),
      args: {
        maximum: schema.maximum
      }
    });
  }

  return errors;
}

function validateInteger(
  schema: IntegerSchema,
  value: string | number | null | undefined,
  required: boolean
): Errors<IntegerSchema> {
  if (typeof schema.const !== 'undefined' && schema.const !== value) {
    return [
      {
        message: getMessageKey('errors.const', schema),
        args: { value: schema.const }
      }
    ];
  }

  const errors = [];

  if (isBlank(value) || Number.isNaN(value as any)) {
    if (!required) {
      return [];
    }

    errors.push(getMessageKey('errors.required', schema));
    return errors;
  }

  const numericValue = parseFloat(`${value}`);

  if (!Number.isInteger(numericValue)) {
    errors.push(getMessageKey('errors.notAnInteger', schema));
  }

  if (schema.minimum && numericValue < schema.minimum) {
    errors.push({
      message: getMessageKey('errors.minimum', schema),
      args: {
        minimum: schema.minimum
      }
    });
  }

  if (schema.maximum && numericValue > schema.maximum) {
    errors.push({
      message: getMessageKey('errors.maximum', schema),
      args: {
        maximum: schema.maximum
      }
    });
  }

  return errors;
}

function validateArray(
  schema: ArraySchema,
  value: Array<any> | null | undefined
): Errors<ArraySchema> {
  const errors: Errors<ArraySchema> = {
    all: [],
    items: []
  };

  if (
    schema.minItems &&
    schema.minItems > 0 &&
    (value || []).length < schema.minItems
  ) {
    errors.all.push({
      message: getMessageKey('errors.minItems', schema),
      args: {
        count: schema.minItems
      }
    });
  }

  if (schema.maxItems && (value || []).length > schema.maxItems) {
    errors.all.push({
      message: getMessageKey('errors.maxItems', schema),
      args: {
        count: schema.maxItems
      }
    });
  }

  if (schema.contains) {
    const contains = schema.contains as Schema;
    if (
      !Array.isArray(value) ||
      value.every(item => hasErrors(validate(contains, item, false)))
    ) {
      errors.all.push(getMessageKey(`errors.notContains`, schema));
    }
  }

  if (schema.not && (schema.not as ArraySchema).contains) {
    const contains = (schema.not as ArraySchema).contains as Schema;
    if (
      !Array.isArray(value) ||
      value.some(item => !hasErrors(validate(contains, item, false)))
    ) {
      errors.all.push(getMessageKey(`errors.contains`, schema));
    }
  }

  if (value && value.length > 0) {
    errors.items = value.map(item => {
      const itemSchema = evaluateSubschema(schema.items, item);
      const result = validate(itemSchema, item, false);

      if (isObjectSchema(itemSchema)) {
        return {
          _id: item._id,
          ...(result as any)
        };
      }

      return result;
    });
  }

  return errors;
}

function validateObject(
  schema: ObjectSchema,
  value: { [key: string]: unknown }
): Errors<ObjectSchema> {
  const properties = schema.properties || {};
  const errors = Object.keys(properties).reduce<Errors<ObjectSchema>>(
    (err, propertyName) => {
      err[propertyName] = validate(
        properties[propertyName],
        (value || {})[propertyName],
        (schema.required || []).includes(propertyName)
      );
      return err;
    },
    { _all: [] }
  );

  if (schema.anyOf) {
    let valid = false;
    // eslint-disable-next-line no-restricted-syntax
    for (const subschema of schema.anyOf) {
      if (!hasErrors(validateObject(subschema, value))) {
        valid = true;
        break;
      }
    }

    if (!valid) {
      errors._all = [getMessageKey('errors.anyOf', schema)];
    }
  }

  return errors;
}

export function validateNull(
  schema: NullSchema,
  value: unknown
): Errors<NullSchema> {
  if (!value) {
    return [];
  }

  return [getMessageKey('errors.shouldBeAbsent', schema)];
}

const valueCache = new WeakMap();
const errorsCache = new WeakMap();

function validate(
  schema: Schema,
  value: any,
  required: boolean
): Errors<Schema> {
  if (schema === null || schema === undefined) {
    return [];
  }

  const memoize = (fn: () => any) => {
    const currentValue = valueCache.get(schema);
    if (currentValue !== undefined && currentValue === value) {
      return errorsCache.get(schema);
    }

    const errors = fn();

    valueCache.set(schema, value);
    errorsCache.set(schema, errors);

    return errors;
  };

  if (isObjectSchema(schema)) {
    return validateObject(schema, value);
  }

  if (isStringSchema(schema)) {
    return memoize(() => validateString(schema, value, required));
  }

  if (isBooleanSchema(schema)) {
    return memoize(() => validateBoolean(schema, value, required));
  }

  if (isNumberSchema(schema)) {
    return memoize(() => validateNumber(schema, value, required));
  }

  if (isIntegerSchema(schema)) {
    return memoize(() => validateInteger(schema, value, required));
  }

  if (isNullSchema(schema)) {
    return validateNull(schema, value);
  }

  if (isArraySchema(schema)) {
    return validateArray(schema, value);
  }

  throw new Error(
    `validateSchema() got invalid schema ${JSON.stringify(schema)}`
  );
}

export default function validateSchema(
  schema: Schema,
  value: unknown
): Errors<Schema> {
  return validate(schema, value, true);
}

function getMessageKey(defaultKey: string, schema: Schema) {
  if (
    schema.$uiOptions &&
    schema.$uiOptions.errorMessages &&
    schema.$uiOptions.errorMessages[defaultKey]
  ) {
    return schema.$uiOptions.errorMessages[defaultKey];
  }

  return defaultKey;
}

/**
 * Function used to refine error messages that has a format string
 * and arguments.
 *
 * The leaf nodes of the Errors type structure are either a string
 * or an object containing the message string and an arbitrary amount
 * of arguments that can be used to make parameterized error strings
 * such as "{count} elements are required.".
 *
 * This function will aid TypeScript in the detection of the latter
 * while traversing an Errors structure.
 *
 * @param Errors errors
 * @returns boolean
 */
function isMessageWithArgs(errors: any): errors is MessageWithArgs {
  return !!(
    errors &&
    typeof errors === 'object' &&
    !Array.isArray(errors) &&
    errors.message &&
    errors.args
  );
}

/**
 * Traverse an Errors data structure and figure out
 * if it has any errors.
 *
 * Errors is the type of structure you get from calling
 * our schema validation functions.
 *
 * @todo: Maybe keep track of this flag _during_ validation
 * and make the validators return both the errors and a flag
 * indicating whether it is valid or not to remove the need
 * to traverse the result.
 *
 * @param errors
 */
export function hasErrors(errors: any): boolean {
  if (Array.isArray(errors)) {
    if (errors.length === 0) {
      return false;
    }

    return errors.some(error => hasErrors(error));
  }

  if (errors == null) {
    return false;
  }

  if (isMessageWithArgs(errors)) {
    return true;
  }

  if (typeof errors === 'object') {
    return Object.keys(errors).some(key => hasErrors(errors[key]));
  }

  if (typeof errors === 'string') {
    return true;
  }

  return false;
}
